import { type FC } from "react";
import { Label, Tooltip } from "flowbite-react";
import { Button } from "@components";
import { HiInformationCircle } from "react-icons/hi";
import { handleNestedField } from "../../helpers/handleNestedFields";

interface FormFieldProp {
  id: string;
  label?: React.ReactNode | undefined;
  tooltip?: string | undefined;
  input: React.ReactNode;
  hidden?: boolean | false;
  isToggle?: boolean | false;
  grid_style?: string | undefined;
}

export interface StandardFormProp {
  buttonConfirmColor?: string;
  buttonConfirmText?: string;
  buttonConfirmPrependIcon?: React.ReactNode;
  buttonCloseText?: string;
  buttonClosePrependIcon?: React.ReactNode;
  hideButtons?: boolean;
  /** Hide only the secondary cancel/close button (keeps the confirm button). */
  hideCancelButton?: boolean;
  onCancel?: () => void;
  onConfirm?: (data: any) => void;
  showHeader?: boolean | false;
  header?: React.ReactNode;
  toggle?: FormFieldProp;
  fields: Array<FormFieldProp>;
  grid_style?: string;
  description?: string; // New optional description prop
  isSubmitting?: boolean;
}

export const StandardForm: FC<StandardFormProp> = (props) => {
  function handleSubmit(event: any) {
    // Prevent default behavior
    event.preventDefault();

    const formData = new FormData(event.target);
    var data = {};

    props.fields.forEach((field) => {
      // Bypass hidden fields
      if (field.hidden) return;

      const fieldId = field.id;
      let fieldData: any = formData.getAll(field.id);
      if (field.isToggle) {
        fieldData = fieldData.length === 1 && fieldData[0] === "on";
      } else if (fieldData.length === 1) {
        fieldData = fieldData[0];
      }

      data = handleNestedField(data, fieldId, fieldData);
    });

    if (props.onConfirm) props.onConfirm(data);
  }

  const dialogContent = (
    <div>
      {props.showHeader ? <div className="mb-0">{props.header}</div> : null}
      {props.description ? (
        <div className="text-secondary dark:text-white text-base pb-2 ">
          {props.description}
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="pt-4">
        <div
          className={
            props.grid_style ? props.grid_style : "grid grid-cols-2 gap-4"
          }
        >
          {props.toggle ? (
            <div className="col-span-full">
              <div key={props.toggle.id}>{props.toggle.input}</div>
            </div>
          ) : null}
          {props.fields.map((field) => {
            if (field.hidden) return null;
            return (
              <div key={field.id} className={field.grid_style ?? ""}>
                {field.label ? (
                  <div className="mb-2 block flex items-center">
                    <Label htmlFor={field.id}>{field.label}</Label>
                    {field.tooltip ? (
                      <Tooltip content={field.tooltip}>
                        <HiInformationCircle className="ml-1 h-4 w-4 text-muted" />
                      </Tooltip>
                    ) : null}
                  </div>
                ) : null}
                {field.input}
              </div>
            );
          })}
        </div>
        {!props.hideButtons && (
          <div className="pt-6 flex gap-4">
            <Button
              color={
                props.buttonConfirmColor ? props.buttonConfirmColor : "primary"
              }
              type="submit"
              isProcessing={props.isSubmitting}
              disabled={props.isSubmitting}
            >
              {props.isSubmitting ? null : props.buttonConfirmPrependIcon}
              {props.buttonConfirmText}
            </Button>
            {!props.hideCancelButton && (
              <Button
                color="gray"
                onClick={() => (props.onCancel ? props.onCancel() : null)}
                disabled={props.isSubmitting}
              >
                {props.buttonClosePrependIcon}
                {props.buttonCloseText}
              </Button>
            )}
          </div>
        )}
      </form>
    </div>
  );

  return <div className="pb-4">{dialogContent}</div>;
};
